import React, { Component } from 'react'
import PropTypes from 'prop-types'
import compose from 'recompose/compose'
import inflection from 'inflection'
import { connect } from 'react-redux'

import Card from '@/ui/Card/Card'
import themed from '@/ui/Theme/themed'

import queryReducer from '@/state/reducers/resource/list/queryReducer'
import { SET_SORT, SET_PAGE, SET_FILTER, SORT_DESC } from '@/state/types/query'
import DefaultPagination from './Pagination'
// import DefaultActions from './Actions'
import { crudGetList as crudGetListAction } from '@/state/actions/data'
import { changeListParams as changeListParamsAction } from '@/state/actions/list'
import translate from '@/i18n/translate'
import removeKey from '@/helpers/removeKey'

import { goToPage as goToPageAction } from '@/state/actions/routes'

/**
 * List page component
 *
 * The <List> component renders the list layout (title, buttons, filters, pagination),
 * and fetches the list of records from the REST API.
 * It then delegates the rendering of the list of records to its child component.
 * Usually, it's a <Datagrid>, responsible for displaying a table with one row for each post.
 *
 * In Redux terms, <List> is a connected component, and <Datagrid> is a dumb component.
 *
 * Props:
 *   - title
 *   - perPage
 *   - sort
 *   - filter (the permanent filter to apply to the query)
 *   - actions
 *   - filters (a React Element used to display the filter form)
 *   - pagination
 *
 * @example
 *     const PostFilter = (props) => (
 *         <Filter {...props}>
 *             <TextInput label="Search" source="q" alwaysOn />
 *             <TextInput label="Title" source="title" />
 *         </Filter>
 *     );
 *     export const PostList = (props) => (
 *         <List {...props}
 *             title="List of posts"
 *             sort={{ field: 'published_at' }}
 *             filter={{ is_published: true }}
 *             filters={<PostFilter />}
 *         >
 *             <Datagrid>
 *                 <TextField source="id" />
 *                 <TextField source="title" />
 *                 <EditButton />
 *             </Datagrid>
 *         </List>
 *     );
 */
export class List extends Component {
  constructor(props) {
    super(props)
    this.state = { key: 0 }
  }

  // shouldComponentUpdate(nextProps, nextState) {
  //   if (
  //     nextProps.isLoading === this.props.isLoading &&
  //     nextProps.width === this.props.width &&
  //     nextState === this.state
  //   ) {
  //     return false
  //   }
  //   return true
  // }

  refresh = event => {
    event.stopPropagation()
    this.updateData()
  }

  /**
     * Merge list params from 3 different sources:
     *   - the query string
     *   - the params stored in the state (from previous navigation)
     *   - the props passed to the List component
     */
  getQuery() {
    const query = { ...this.props.params }
    if (!query.sort) {
      query.sort = this.props.sort.field
      query.order = this.props.sort.order
    }
    if (!query.perPage) {
      query.perPage = this.props.perPage
    }
    return query
  }

  filterQuery(query) {
    // const { currentRouteData: { defaultParams } } = this.props
    const defaultParams = this.props.params.default
    return {
      ...(query.sort && query.sort !== defaultParams.sort && { sort: query.sort }),
      ...(query.order && query.order !== defaultParams.order && { order: query.order }),
      ...(query.page && query.page != defaultParams.page && { page: query.page }),
      ...(query.perPage && query.perPage != defaultParams.perPage && { perPage: query.perPage }),
    }
  }

  updateData(query) {
    const params = query || this.getQuery()
    const { sort, order, page, perPage, filter } = params
    const pagination = { page: parseInt(page, 10), perPage: parseInt(perPage, 10) }
    const permanentFilter = this.props.filter
    this.props.crudGetList(
      this.props.resource,
      pagination,
      { field: sort, order },
      { ...filter, ...permanentFilter },
    )
  }

  setSort = sort => this.changeParams({ type: SET_SORT, payload: sort })

  setPage = page => this.changeParams({ type: SET_PAGE, payload: page })

  setFilters = filters => this.changeParams({ type: SET_FILTER, payload: filters })

  showFilter = (filterName, defaultValue) => {
    this.setState({ [filterName]: true })
    if (typeof defaultValue !== 'undefined') {
      this.setFilters({ ...this.props.filterValues, [filterName]: defaultValue })
    }
  }

  hideFilter = filterName => {
    this.setState({ [filterName]: false })
    const newFilters = removeKey(this.props.filterValues, filterName)
    this.setFilters(newFilters)
  }

  changeParams(action) {
    const newParams = queryReducer(this.getQuery(), action)
    this.props.changeListParams(this.props.resource, newParams)
    this.props.goToPage(this.props.currentRouteType, this.filterQuery(newParams))
  }

  render() {
    const {
      filters,
      pagination = <DefaultPagination />,
      actions, // = <DefaultActions />,
      resource,
      hasCreate,
      title,
      data,
      ids,
      total,
      theme,
      children,
      isLoading,
      translate,
    } = this.props
    const query = this.getQuery()
    const filterValues = query.filter

    const resourceName = translate(`resources.${resource}.name`, {
      smart_count: 2,
      _: inflection.humanize(inflection.pluralize(resource)),
    })
    const defaultTitle = translate('i18n.page.list', { name: `${resourceName}` })
    const header = (
      <If condition={title !== false && filters}>
        <If condition={title !== false}>
          <Choose>
            <When condition={title}>
              <span key={0}>{title}</span>
            </When>
            <Otherwise>
              <span key={0}>{defaultTitle}</span>
            </Otherwise>
          </Choose>
        </If>
        <If condition={filters}>
          {React.cloneElement(filters, {
            resource,
            theme,
            key: 1,
            hideFilter: this.hideFilter,
            filterValues,
            displayedFilters: this.state,
            setFilters: this.setFilters,
            context: 'form',
          })}
        </If>
      </If>
    )

    return [
      <Card
        style={{ opacity: isLoading ? 0.8 : 1 }}
        header={header}
        key={0}
        theme={theme}
        classes="bottom-margin-double"
      >
        <If condition={actions}>
          <div>
            {React.cloneElement(actions, {
              resource,
              theme,
              filters,
              filterValues,
              hasCreate,
              displayedFilters: this.state,
              showFilter: this.showFilter,
              refresh: this.refresh,
            })}
          </div>
        </If>
        <Choose>
          <When condition={isLoading || total > 0}>
            <div style={{ margin: '0 -24px 0 -24px' }}>
              <If condition={children}>
                {React.cloneElement(children, {
                  resource,
                  theme,
                  ids,
                  data,
                  currentSort: { field: query.sort, order: query.order },
                  isLoading,
                  setSort: this.setSort,
                })}
              </If>
            </div>
          </When>
          <Otherwise>
            <div>{translate('i18n.navigation.no_results')}</div>
          </Otherwise>
        </Choose>
      </Card>,
      <If condition={pagination && (isLoading || total > 0)}>
        {React.cloneElement(pagination, {
          total,
          theme,
          key: 1,
          showRange: false,
          nbLabels: false,
          page: parseInt(query.page, 10),
          perPage: parseInt(query.perPage, 10),
          setPage: this.setPage,
        })}
      </If>,
    ]
  }
}

List.propTypes = {
  // the props you can change
  title: PropTypes.any,
  filter: PropTypes.object,
  filters: PropTypes.element,
  pagination: PropTypes.element,
  actions: PropTypes.element,
  perPage: PropTypes.number.isRequired,
  sort: PropTypes.shape({
    field: PropTypes.string,
    order: PropTypes.string,
  }),
  children: PropTypes.element.isRequired,
  // the props managed by system
  changeListParams: PropTypes.func.isRequired,
  crudGetList: PropTypes.func.isRequired,
  currentRouteType: PropTypes.string.isRequired,
  data: PropTypes.object,
  filterValues: PropTypes.object,
  hasCreate: PropTypes.bool,
  ids: PropTypes.array,
  isLoading: PropTypes.bool.isRequired,
  path: PropTypes.string,
  params: PropTypes.object.isRequired,
  resource: PropTypes.string.isRequired,
  total: PropTypes.number.isRequired,
  translate: PropTypes.func.isRequired,
}

List.defaultProps = {
  filter: {},
  filterValues: {},
  hasCreate: false,
  perPage: 10,
  sort: {
    field: 'id',
    order: SORT_DESC,
  },
}

function mapStateToProps(state, props) {
  const resourceState = state.rest[props.resource]
  return {
    params: resourceState.list.params,
    ids: resourceState.list.ids,
    total: resourceState.list.total,
    data: resourceState.data,
    isLoading: state.rest.loading > 0,
    filterValues: resourceState.list.params.filter,
  }
}

const enhance = compose(
  connect(mapStateToProps, {
    crudGetList: crudGetListAction,
    changeListParams: changeListParamsAction,
    goToPage: goToPageAction,
  }),
  translate,
  themed,
)

export default enhance(List)



// WEBPACK FOOTER //
// ./ui/DataTable/List.js